import {
	Anchor,
	ConfirmationDialog,
	FormattedText,
	Typography
} from "client/Components";
import { useDataInputDialog } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";

export type ChangeUserLicenseConfirmationDialogProps = {
	title: string;
	onConfirm(): void;
	onCancel(): void;
	trackingId: string;
};

export const ChangeUserLicenseConfirmationDialog = (
	props: ChangeUserLicenseConfirmationDialogProps
): ReactElement => {
	const {
		title,
		onConfirm,
		onCancel,
		trackingId
	} = props;

	return (
		<ConfirmationDialog
			title={title}
			confirmText={localize("_Confirm")}
			onConfirmation={onConfirm}
			onCancellation={onCancel}
			trackingId={trackingId}
		>
			<FormattedText
				text={localize("_UserLicenseChangeInformationMessage")}
				thingsToReplace={[
					{
						what: "{{ClickHere}}",
						filler: (
							<Anchor
								href="https://support.workspace365.net/hc/en-us/articles/360012676120-Billing-Subscription"
								target="_blank"
								trackingId="support-link"
							>
								{localize("_ClickHere")}
							</Anchor>
						)
					},
					{
						what: "{{Note}}",
						filler: (
							<Typography weight="bold">
								{localize("_Note")}
							</Typography>
						)
					}
				]}
			/>
		</ConfirmationDialog>
	);
};
ChangeUserLicenseConfirmationDialog.displayName = "ChangeUserLicenseConfirmationDialog";

export type ChangeUserLicenseConfirmationDialogConfigurationData = Omit<
	ChangeUserLicenseConfirmationDialogProps,
	"onConfirm" | "onCancel"
>;

export function useChangeUserLicenseConfirmationDialog(
	configurationData: ChangeUserLicenseConfirmationDialogConfigurationData
): [
	element: ReactElement | null,
	open: () => Promise<boolean>
] {
	const [element, originalOpen] = useDataInputDialog<undefined, void>(
		(_, onCancel, onConfirm) => (
			<ChangeUserLicenseConfirmationDialog
				{...configurationData}
				onConfirm={onConfirm}
				onCancel={onCancel}
			/>
		)
	);

	const open = useCallback(
		async (): Promise<boolean> => {
			return (await originalOpen(undefined)).wasSuccessful;
		},
		[originalOpen]
	);

	return [element, open];
}